/**
 * Authenticated File Preview Utility
 *
 * Securely opens protected files in a new tab by fetching with
 * the JWT bearer token and converting to a Blob URL.
 *
 * This replaces raw `window.open(file.url, '_blank')` calls which
 * fail because the browser does not attach the Authorization header
 * when opening a new tab/window.
 *
 * Jun 14, 2026 — FILES-PREVIEW-001 hardening:
 *   • Open the new tab SYNCHRONOUSLY during the user gesture so
 *     Chrome / Safari popup blockers don't silently kill the tab
 *     (the previous version opened the tab AFTER `await fetch()`
 *     which drops the user-gesture token and led to stuck
 *     "Loading … for preview…" toasts when the popup was blocked
 *     in some browsers / privacy modes).
 *   • Hard 30s timeout via AbortController so the toast cannot
 *     hang forever if the backend stalls.
 *   • Single `finally` cleanup so the loading toast ALWAYS resolves
 *     to success / error and never lingers.
 */

import { toast } from 'sonner';

const PREVIEW_TIMEOUT_MS = 30000;

export async function openAuthenticatedFile(url, filename) {
  const label = filename || 'file';
  const toastId = toast.loading(`Loading ${label} for preview...`);

  // Step 1 — Open the new tab SYNCHRONOUSLY during the click so
  // the browser still has the user-gesture token. We point it at
  // about:blank initially and inject a tiny "Loading…" placeholder
  // so the user sees something while the blob is fetched.
  // Note: we intentionally DO NOT pass 'noopener' here because we
  // need a handle on the window to navigate it once the blob is
  // ready; we sever `opener` after navigation for safety.
  const newTab = window.open('', '_blank');
  if (!newTab) {
    toast.error('Please allow popups to preview files.', { id: toastId });
    return;
  }
  try {
    newTab.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading ${label}…</title></head>` +
      `<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,-apple-system,sans-serif;color:#475569;background:#f8fafc">` +
      `<div style="text-align:center"><div style="font-size:14px;font-weight:500">Loading ${label}…</div></div></body></html>`
    );
  } catch (_) { /* about:blank write can fail under some CSPs; safe to ignore */ }

  let blobUrl = null;
  let toastResolved = false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PREVIEW_TIMEOUT_MS);

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Unable to preview file. Please log in again.', { id: toastId });
      toastResolved = true;
      newTab.close();
      return;
    }

    const resolvedUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    const response = await fetch(resolvedUrl, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });

    if (!response.ok) {
      if (response.status === 403) {
        toast.error('You do not have permission to view this file.', { id: toastId });
      } else if (response.status === 404) {
        toast.error('File not found or has been deleted.', { id: toastId });
      } else {
        toast.error('Unable to preview file. Please try again.', { id: toastId });
      }
      toastResolved = true;
      newTab.close();
      return;
    }

    const blob = await response.blob();
    blobUrl = URL.createObjectURL(blob);

    // Step 2 — Point the already-open tab at the blob URL.
    try {
      newTab.location.replace(blobUrl);
    } catch (_) {
      // Fallback in case `location.replace` is blocked by the
      // tab's origin policy after about:blank.
      newTab.location.href = blobUrl;
    }
    // Sever the opener relationship after navigation for safety
    // (the new tab is now showing the blob and has no legitimate
    // reason to keep a reference back to our window).
    try { newTab.opener = null; } catch (_) { /* cross-origin after navigation is ok */ }

    // Jun 14, 2026 — FILES-TOAST-001: per the UX sweep, file-open
    // success toasts are redundant noise (the file already opened in
    // a new tab — the user can see it). Dismiss the loading toast
    // silently. Errors below still surface their own toast.
    toast.dismiss(toastId);
    toastResolved = true;
  } catch (err) {
    if (err && err.name === 'AbortError') {
      toast.error(`Preview timed out loading ${label}. Please try again.`, { id: toastId });
    } else {
      console.error('Authenticated file preview failed:', err);
      toast.error(`Unable to preview ${label}. Please try again.`, { id: toastId });
    }
    toastResolved = true;
    try { newTab.close(); } catch (_) { /* noop */ }
  } finally {
    clearTimeout(timer);
    // Safety net: if for any reason the success / error toast was
    // NOT issued (e.g. an unexpected throw before the catch path),
    // dismiss the loading toast so it never lingers on screen.
    if (!toastResolved) {
      try { toast.dismiss(toastId); } catch (_) { /* noop */ }
    }
    // Revoke after a generous delay so the new tab fully renders
    // (PDFs / large PNGs may still be streaming into the viewer).
    if (blobUrl) {
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch (_) { /* noop */ }
      }, 60000);
    }
  }
}
